import { request } from '@/utils/http'
import { UserType } from '@/enums/auth'

// ===================== 类型定义 =====================

/**
 * 用户详情返回结构（租户端）
 */
export interface TenantUserDetailVO {
  id: string
  username: string
  realName: string
  phone: string
  email: string
  avatar?: string
  userType: number // 用户类型 1-租户用户 2-客户用户
  status: number // 状态 1-启用 0-禁用
  roleType: number // 用户角色，1-超级管理员，2-普通用户
  tenantId: string
  enterpriseId: string
  enterpriseName: string
  enterpriseNameEn?: string
  tenantName?: string
  branchId?: string | number
  branchName?: string
  /** 英文名称（员工管理中的英文名） */
  englishName?: string
  /** 座机电话国家代码 */
  landlineAreaCode?: string
  /** 座机电话区号 */
  landlineNumber?: string
  /** 座机电话号码 */
  landlineExtension?: string
  createTime: string
  updateTime: string
  loginTime?: string
  offices?: any[] // 所属分公司列表
  departments?: any[] // 所属部门列表
  roles?: any[] // 所属角色列表
  leaders?: any[] // 上级主管列表
  tenantUserThirdPartyRelVOList?: TenantUserThirdPartyRelVO[] // 第三方账号
}

/**
 * 用户详情返回结构（客户端）
 */
export interface ClientUserDetailVO {
  id: string
  username: string
  password?: string
  phone: string
  email: string
  avatar: string
  userType: number
  status: number
  dateCreated: string
  loginTime?: string
  enterpriseId: string
  enterpriseName: string
  enterpriseNameEn?: string
  tenantId?: string
  tenantName?: string
  branchId?: string | number
  branchName?: string
  roleType: number // 角色类型 0-普通用户 1-管理员
}

export interface TenantUserThirdPartyRelVO {
  id: string
  userId: string
  thirdPartyUserId: number
  thirdPartyTenantId: number
  thirdPartyType: number
}

/**
 * 第三方租户绑定请求体
 */
export interface ThirdBindRequest {
  account: string // 手机号/邮箱
  password: string // 密码
  tenantId?: number // 选择第三方租户ID（可选）
  platform: number // 来源, eto/cw
}

/**
 * 第三方平台租户信息
 */
export interface TenantVO {
  uuid: string
  tenantId: number
  name: string
  englishName: string
  address: string
  phone: string
  email: string
}

/**
 * 绑定第三方返回
 */
export interface ThirdBindVO {
  tenants: TenantVO[] // 下拉租户列表
}

/**y
 * 主键通用请求体
 */
export interface PrimaryKeyRequest {
  id?: string
  ids?: string[]
}

/**
 * 用户更新请求参数
 */
export interface UserUpdateRequest {
  enterpriseId: string // 所属公司ID（必填）
  username: string // 用户名（必填，最大50字符）
  phone: string // 手机号（必填，支持国际格式，最大20字符）
  email: string
  avatar: string// 邮箱（必填，最大100字符）
}

/**
 * 重置密码发送验证码请求参数
 */
export interface ResetPasswordCodeRequest {
  type: number // 手机号或邮箱类型（必填，整数）
}

/**
 * 重置密码请求参数
 */
export interface ResetPasswordRequest {
  type: number // 手机号或邮箱类型（必填，整数）
  verificationCode: string // 手机验证码（必填）
  password: string // 密码（必填，6-100字符）
  confirmPassword: string // 确认密码（必填，6-100字符）
}

/**
 * 发送验证码请求参数
 */
export interface SendVerificationCodeRequest {
  type: number // 1-手机号 2-邮箱
  account: string // 手机号或邮箱
  skipUserVerification?: boolean
}

/**
 * 验证验证码请求参数
 */
export interface VerifyVerificationCodeRequest {
  type: number // 1-手机号 2-邮箱
  account: string // 手机号或邮箱
  verificationCode: string // 验证码
}

// ===================== 租户端用户中心接口 =====================

/**
 * 租户端-获取用户详情
 * @returns Promise<TenantUserDetailVO> 用户详情
 */
export const getTenantUserDetail = (): Promise<TenantUserDetailVO> => {
  return request.post<TenantUserDetailVO>('/enterprise/tenant/user/detail')
}

/**
 * 租户端-更新用户信息
 * @param params 用户更新参数
 * @returns Promise<TenantUserDetailVO> 更新后的用户详情
 */
export const updateTenantUser = (params: UserUpdateRequest): Promise<TenantUserDetailVO> => {
  return request.post<TenantUserDetailVO>('/enterprise/tenant/user/update', params)
}

/**
 * 租户端-重置密码发送验证码
 * @param params 发送验证码参数
 * @returns Promise<void>
 */
export const sendTenantResetPasswordCode = (params: ResetPasswordCodeRequest): Promise<void> => {
  return request.post<void>('/enterprise/tenant/user/resetPassword/code', params)
}

/**
 * 租户端-重置密码
 * @param params 重置密码参数
 * @returns Promise<void>
 */
export const resetTenantPassword = (params: ResetPasswordRequest): Promise<void> => {
  return request.post<void>('/enterprise/tenant/user/resetPassword', params)
}

// ===================== 客户端用户中心接口 =====================

/**
 * 客户端-获取用户详情
 * @returns Promise<ClientUserDetailVO> 用户详情
 */
export const getClientUserDetail = (): Promise<ClientUserDetailVO> => {
  return request.post<ClientUserDetailVO>('/enterprise/client/user/detail')
}

/**
 * 客户端-更新用户信息
 * @param params 用户更新参数
 * @returns Promise<ClientUserDetailVO> 更新后的用户详情
 */
export const updateClientUser = (params: UserUpdateRequest): Promise<ClientUserDetailVO> => {
  return request.post<ClientUserDetailVO>('/enterprise/client/user/update', params)
}

/**
 * 客户端-重置密码发送验证码
 * @param params 发送验证码参数
 * @returns Promise<void>
 */
export const sendClientResetPasswordCode = (params: ResetPasswordCodeRequest): Promise<void> => {
  return request.post<void>('/enterprise/client/user/resetPassword/code', params)
}

/**
 * 客户端-重置密码
 * @param params 重置密码参数
 * @returns Promise<void>
 */
export const resetClientPassword = (params: ResetPasswordRequest): Promise<void> => {
  return request.post<void>('/enterprise/client/user/resetPassword', params)
}

/**
 * 发送验证码
 * @param params
 * @returns Promise<void>
 */
export const tenantSendVerificationCode = (params: SendVerificationCodeRequest): Promise<void> => {
  return request.post<void>('/enterprise/tenant/user/sendVerificationCode', params)
}

/**
 * 验证验证码
 * @param params
 * @returns Promise<void>
 */
export const tenantVerifyVerificationCode = (params: VerifyVerificationCodeRequest): Promise<void> => {
  return request.post<void>('/enterprise/tenant/user/verifyVerificationCode', params)
}

/**
 * 发送验证码
 * @param params
 * @returns Promise<void>
 */
export const clientSendVerificationCode = (params: SendVerificationCodeRequest): Promise<void> => {
  return request.post<void>('/enterprise/client/user/sendVerificationCode', params)
}

/**
 * 验证验证码
 * @param params
 * @returns Promise<void>
 */
export const clientVerifyVerificationCode = (params: VerifyVerificationCodeRequest): Promise<void> => {
  return request.post<void>('/enterprise/client/user/verifyVerificationCode', params)
}

// ===================== 第三方租户绑定相关接口 =====================

/**
 * 绑定第三方租户
 * @param params ThirdBindRequest
 * @returns Promise<ThirdBindVO>
 */
export const tenantEnterpriseThirdBind = (params: ThirdBindRequest): Promise<ThirdBindVO> => {
  return request.post<ThirdBindVO>('/enterprise/tenant/enterprise/third/bind', params)
}

/**
 * 解绑第三方租户
 * @param params PrimaryKeyRequest
 * @returns Promise<void>
 */
export const tenantEnterpriseThirdUnbind = (params: PrimaryKeyRequest): Promise<void> => {
  return request.post<void>('/enterprise/tenant/enterprise/third/unbind', params)
}

// ===================== 操作公司相关接口 =====================

/**
 * 操作公司信息（用于销售分公司）
 */
export interface OperatingCompanyVO {
  id: string
  enterpriseCode: string
  enterpriseName: string
  enterpriseNameEn?: string
  businessLicense?: string
  status: number
  enterpriseType: number
  dateCreated?: string
}

/**
 * 获取当前登录用户所属的所有操作公司（销售分公司）
 * @returns Promise<OperatingCompanyVO[]> 操作公司列表
 */
export const listOperatingCompanies = (): Promise<OperatingCompanyVO[]> => {
  return request.post<OperatingCompanyVO[]>('/enterprise/tenant/user/operating-companies')
}

// ===================== 通用方法 =====================

/**
 * 根据用户类型获取用户详情
 * @param userType 用户类型：UserType.TENANT-租户用户，UserType.CLIENT-客户用户
 * @returns Promise<TenantUserDetailVO | ClientUserDetailVO> 用户详情
 */
export const getUserDetail = (userType: UserType): Promise<TenantUserDetailVO | ClientUserDetailVO> => {
  if (userType === UserType.TENANT) {
    return getTenantUserDetail()
  } else {
    return getClientUserDetail()
  }
}

/**
 * 根据用户类型更新用户信息
 * @param userType 用户类型：UserType.TENANT-租户用户，UserType.CLIENT-客户用户
 * @param params 用户更新参数
 * @returns Promise<TenantUserDetailVO | ClientUserDetailVO> 更新后的用户详情
 */
export const updateUser = (
  userType: UserType,
  params: UserUpdateRequest
): Promise<TenantUserDetailVO | ClientUserDetailVO> => {
  if (userType === UserType.TENANT) {
    return updateTenantUser(params)
  } else {
    return updateClientUser(params)
  }
}

/**
 * 根据用户类型发送重置密码验证码
 * @param userType 用户类型：UserType.TENANT-租户用户，UserType.CLIENT-客户用户
 * @param params 发送验证码参数
 * @returns Promise<void>
 */
export const sendResetPasswordCode = (
  userType: UserType,
  params: ResetPasswordCodeRequest
): Promise<void> => {
  if (userType === UserType.TENANT) {
    return sendTenantResetPasswordCode(params)
  } else {
    return sendClientResetPasswordCode(params)
  }
}

/**
 * 根据用户类型重置密码
 * @param userType 用户类型：UserType.TENANT-租户用户，UserType.CLIENT-客户用户
 * @param params 重置密码参数
 * @returns Promise<void>
 */
export const resetPassword = (
  userType: UserType,
  params: ResetPasswordRequest
): Promise<void> => {
  if (userType === UserType.TENANT) {
    return resetTenantPassword(params)
  } else {
    return resetClientPassword(params)
  }
}

/**
 * 根据用户类型发送验证码
 * @param userType 用户类型：UserType.TENANT-租户用户，UserType.CLIENT-客户用户
 * @param params 发送验证码参数
 * @returns Promise<void>
 */
export const sendVerificationCodeByUserType = (
  userType: UserType,
  params: SendVerificationCodeRequest
): Promise<void> => {
  if (userType === UserType.TENANT) {
    return tenantSendVerificationCode(params)
  } else {
    return clientSendVerificationCode(params)
  }
}

/**
 * 根据用户类型验证验证码
 * @param userType 用户类型：UserType.TENANT-租户用户，UserType.CLIENT-客户用户
 * @param params 验证验证码参数
 * @returns Promise<void>
 */
export const verifyVerificationCodeByUserType = (
  userType: UserType,
  params: VerifyVerificationCodeRequest
): Promise<void> => {
  if (userType === UserType.TENANT) {
    return tenantVerifyVerificationCode(params)
  } else {
    return clientVerifyVerificationCode(params)
  }
}
